/**
 * Основные функции для работы с AMO-полями в карточке сделки.
 *
 * Модуль изолирует чтение метаданных и текущих значений из глобального AMOCRM API.
 * Его используют настройки чеклистов, синхронизация значений и проверки заполненности,
 * поэтому здесь важно хранить AMO enum id как техническое значение, а человекочитаемый
 * label получать отдельно на границе отображения.
 */

const AMO_ENUM_LABEL_KEYS = [
    "VALUE",
    "~VALUE",
    "value",
    "~value",
    "label",
    "LABEL",
    "name",
    "NAME",
    "title",
    "TITLE",
]

/**
 * Нормализация названия поля для сопоставления
 * @param {string} fieldName - Исходное название поля
 * @returns {string} Нормализованное название (без пробелов, в нижнем регистре)
 */
export function normalizeFieldName(fieldName) {
    if (!fieldName || typeof fieldName !== 'string') {
        return '';
    }
    
    return fieldName
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '')
        .replace(/[^\w\u0400-\u04FF]/g, ''); 
}

/**
 * Получение всех AMO полей из системы
 * @returns {Array} Массив объектов AMO полей с нормализованными данными
 */
export function getAllAmoFields() {
    try {
        const amoFields = AMOCRM.constant("account").cf || {};
        
        return Object.values(amoFields).map(field => ({
            id: field.ID,
            name: field.NAME,
            normalizedName: normalizeFieldName(field.NAME),
            typeId: field.TYPE_ID,
            type: field.TYPE_CODE || getAmoFieldType(field.TYPE_ID), 
            enums: field.ENUMS || {},
            isSystem: field.CODE ? true : false,
            code: field.CODE || null
        }));
    } catch (error) {
        console.error('Ошибка при получении AMO полей:', error);
        return [];
    }
}

/**
 * Получение человекочитаемой подписи enum-значения AMO без потери технического id.
 *
 * AMOCRM в разных местах отдает enum metadata неединообразно: строкой, объектом
 * с `VALUE`, объектом с `~VALUE`, иногда с lower-case ключами. UI должен показывать
 * подпись, но бизнес-условия и синхронизация продолжают сохранять enum id, потому
 * fallback всегда возвращает `enumId`. Пустые строки и отсутствующие label считаются
 * непригодными для отображения.
 *
 * @param {string|number|Object|null|undefined} enumData - Значение из AMO `ENUMS[id]`.
 * @param {string|number|null|undefined} enumId - Технический enum id для сохранения.
 * @returns {string} Подпись для UI или enum id, если подпись недоступна.
 */
export function getAmoEnumDisplayValue(enumData, enumId) {
    if (enumData === null || enumData === undefined) {
        return enumId === null || enumId === undefined ? "" : String(enumId)
    }

    if (typeof enumData !== "object") {
        const primitiveLabel = String(enumData).trim()
        return primitiveLabel || String(enumId)
    }

    for (const key of AMO_ENUM_LABEL_KEYS) {
        const label = enumData[key]
        if (label === null || label === undefined) {
            continue
        }

        const normalizedLabel = String(label).trim()
        if (normalizedLabel) {
            return normalizedLabel
        }
    }

    return enumId === null || enumId === undefined ? "" : String(enumId)
}

/**
 * Определение типа AMO поля по TYPE_ID
 * @param {number} typeId - ID типа поля в AMO
 * @returns {string} Тип поля (TEXT, SELECT, MULTISELECT, CHECKBOX, DATE, URL, PHONE, EMAIL)
 */
export function getAmoFieldType(typeId) {
    const typeMap = {
        1: 'TEXT',           
        2: 'TEXTAREA',       
        3: 'SELECT',         
        4: 'MULTISELECT',    
        5: 'DATE',           
        6: 'URL',            
        7: 'TEXTAREA',       
        8: 'PHONE',          
        9: 'EMAIL',          
        10: 'SELECT',        
        11: 'CHECKBOX'       
    };
    
    return typeMap[typeId] || 'UNKNOWN';
}

/**
 * Получение текущего значения AMO поля
 * @param {string|number} fieldId - ID поля
 * @returns {*} Текущее значение поля или null
 */
export function getCurrentAmoFieldValue(fieldId) {
    try {
        const attributes = AMOCRM?.data?.current_card?.model?.attributes;
        if (!attributes) {
            return null;
        }
        
        const cfvKey = `CFV[${fieldId}]`;
        const amoFields = AMOCRM.constant("account").cf || {};
        const field = amoFields[fieldId];
        
        if (field && field.TYPE_ID === 4 && field.ENUMS) { 
            const selectedValues = [];
            Object.keys(field.ENUMS).forEach(enumId => {
                const enumKey = `${cfvKey}[${enumId}]`;
                const enumValue = attributes[enumKey];
                
                if (enumValue === enumId) {
                    selectedValues.push(enumId);
                }
            });
            return selectedValues.length > 0 ? selectedValues : null;
        } else {
            const value = attributes[cfvKey];
            return value !== undefined ? value : null;
        }
    } catch (error) {
        console.error(`Ошибка при получении значения AMO поля ${fieldId}:`, error);
        return null;
    }
}

/**
 * Получение текстового представления значения AMO поля
 * @param {string|number} fieldId - ID поля
 * @param {*} value - Значение поля (может быть массивом ID для мультисписка)
 * @returns {string} Текстовое представление значения
 */
export function getAmoFieldValueText(fieldId, value = null) {
    try {
        const actualValue = value !== null ? value : getCurrentAmoFieldValue(fieldId);
        if (!actualValue) {
            return '';
        }
        
        const amoFields = AMOCRM.constant("account").cf || {};
        const field = amoFields[fieldId];
        
        if (!field || !field.ENUMS) {
            return String(actualValue);
        }
        
        if (Array.isArray(actualValue)) {
            return actualValue
                .map(id => getAmoEnumDisplayValue(field.ENUMS[id], id))
                .join(', ');
        }
        
        return getAmoEnumDisplayValue(field.ENUMS[actualValue], actualValue);
    } catch (error) {
        console.error(`Ошибка при получении текста значения AMO поля ${fieldId}:`, error);
        return '';
    }
}
